import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment";
import learnlocalLoader from "assets/images/gifs/learnlocalLoader.gif";
import communityGif from "assets/images/gifs/community.gif";
import dpIcon from "assets/images/others/host-side-images/dpIcon.png";
import { MessageDTO } from "utils/helpers/models/shared/message.dto";
import useAmbassadorCommunityHooks from "./useAmbassadorCommunityHooks";

type AmbassadorChatWindowProps = {
  eventId: number | null;
  messages: MessageDTO[];
  setMessages: (updater: any) => void;
  userProfilePic?: string | null;
  isEventEnded?: boolean;
};

const AmbassadorChatWindow: React.FC<AmbassadorChatWindowProps> = ({
  eventId,
  messages,
  setMessages,
  userProfilePic,
  isEventEnded = false,
}) => {
  const { getCommunityMessages } = useAmbassadorCommunityHooks();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const messagesRef = useRef<MessageDTO[]>(messages);
  const isPollingRef = useRef(false);
  const isFetchingRef = useRef(false);
  const eventIdRef = useRef<number | null>(null);
  const getCommunityMessagesRef = useRef(getCommunityMessages);
  const MSG_PAGE_SIZE = 10;

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const fetchMessages = useCallback(async (isPolling: boolean = false) => {
    const currentEventId = eventIdRef.current;
    if (!currentEventId) return;
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    if (!isPolling) setIsLoading(true);
    try {
      const queryParams = {
        page: 0,
        size: MSG_PAGE_SIZE,
      };
      await getCommunityMessagesRef.current(
        currentEventId,
        queryParams,
        (newMsgs: MessageDTO[]) => {
          const sorted = newMsgs.sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
          if (isPolling) {
            setMessages((prev: MessageDTO[]) => {
              const existingIds = new Set(prev.map((m) => m.id));
              const filtered = sorted.filter((m) => !existingIds.has(m.id));
              if (filtered.length === 0) return prev;
              const updated = [...prev, ...filtered].sort(
                (a, b) =>
                  new Date(a.createdAt).getTime() -
                  new Date(b.createdAt).getTime()
              );
              messagesRef.current = updated;
              return updated;
            });
          } else {
            messagesRef.current = sorted;
            setMessages(sorted);
            // prepare for next page on scroll
            setPage(1);
            setHasMore(sorted.length === MSG_PAGE_SIZE);
          }
        },
        messagesRef.current,
        () => {}
      );
    } finally {
      if (!isPolling) setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  const loadMoreMessages = useCallback(async () => {
    const currentEventId = eventIdRef.current;
    if (!currentEventId || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const el = chatRef.current;
    const beforeHeight = el?.scrollHeight ?? 0;
    const beforeTop = el?.scrollTop ?? 0;
    try {
      const queryParams = {
        page,
        size: MSG_PAGE_SIZE,
      };
      let loadedCount = 0;
      await getCommunityMessagesRef.current(
        currentEventId,
        queryParams,
        (newMsgs: MessageDTO[]) => {
          loadedCount = newMsgs.length;
          const sorted = newMsgs.sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
          setMessages((prev: MessageDTO[]) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const filtered = sorted.filter((m) => !existingIds.has(m.id));
            const merged = [...prev, ...filtered];
            const finalSorted = merged.sort(
              (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            );
            messagesRef.current = finalSorted;
            return finalSorted;
          });
        },
        messagesRef.current,
        () => {}
      );
      setHasMore(loadedCount === MSG_PAGE_SIZE);
      setPage((p) => p + 1);
      // preserve scroll position after prepending older messages
      setTimeout(() => {
        const el2 = chatRef.current;
        if (!el2) return;
        const afterHeight = el2.scrollHeight;
        el2.scrollTop = beforeTop + (afterHeight - beforeHeight);
      }, 0);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, page, setMessages]);

  // Initial load + polling lifecycle
  useEffect(() => {
    eventIdRef.current = eventId;
    getCommunityMessagesRef.current = getCommunityMessages;

    // reset view when eventId changes
    if (eventId) {
      messagesRef.current = [];
      setMessages([]);
      setPage(0);
      setHasMore(true);
      fetchMessages(false);
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      isPollingRef.current = false;
    }

    if (eventId) {
      isPollingRef.current = true;
      intervalRef.current = setInterval(() => {
        if (!isLoading) fetchMessages(true);
      }, 10000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        isPollingRef.current = false;
      }
    };
  }, [eventId]);

  // Auto-scroll when near bottom or when I send
  useEffect(() => {
    if (chatRef.current && messages.length > 0) {
      const el = chatRef.current;
      const isNearBottom =
        el.scrollHeight - el.scrollTop <= el.clientHeight + 150;
      if (isNearBottom || messages[messages.length - 1]?.sentByMe) {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }
    }
  }, [messages]);

  const handleChatScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const threshold = 100;
      // Load more when scrolling near top
      if (el.scrollTop <= threshold && hasMore && !isLoadingMore) {
        loadMoreMessages();
      }
    },
    [loadMoreMessages, hasMore, isLoadingMore]
  );

  const groupedMessages = useMemo(() => {
    const copy = [...messages];
    copy.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    return copy.reduce((acc, message) => {
      const msgDate = moment(message.createdAt);
      const today = moment();
      const yesterday = moment().subtract(1, "day");
      const label = msgDate.isSame(today, "day")
        ? "Today"
        : msgDate.isSame(yesterday, "day")
        ? "Yesterday"
        : msgDate.format("MMMM D, YYYY");
      if (!acc[label]) acc[label] = [];
      acc[label].push(message);
      return acc;
    }, {} as Record<string, MessageDTO[]>);
  }, [messages]);

  if (!eventId) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-gray-500 flex flex-col items-center justify-center">
          <img src={communityGif} alt="community" className="w-20 h-20" />
          <h3 className="text-xl font-semibold mb-2">No Event Selected</h3>
          <p>Select an event to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-3 min-w-0 min-h-0"
        ref={chatRef}
        onScroll={handleChatScroll}
      >
        {/* Loading indicator at top for pagination */}
        {isLoadingMore && (
          <div className="flex justify-center py-2">
            <img
              src={learnlocalLoader}
              className="w-8 h-8"
              alt="Loading more..."
            />
          </div>
        )}

        {isLoading && messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="">
              <img src={learnlocalLoader} className="w-20 h-20" />
            </div>
          </div>
        )}

        {!isLoading && messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500 flex flex-col items-center justify-center">
              <div className="">
                <img src={communityGif} alt="community" className="w-20 h-20" />
              </div>
              <p>
                {isEventEnded
                  ? "No messages available"
                  : "No messages yet"}
              </p>
              <p className="text-sm">
                {isEventEnded
                  ? "This community conversation has ended"
                  : "Start the conversation!"}
              </p>
            </div>
          </div>
        )}

        {messages.length > 0 && (
          <Fragment>
            {Object.entries(groupedMessages).map(([dateLabel, msgs], i) => (
              <Fragment key={i}>
                <div className="flex justify-center items-center text-center w-fit mx-auto text-sm rounded-full px-6 py-2 bg-lightWhiteBg text-lightBlackText my-3 font-semibold">
                  {dateLabel}
                </div>
                {msgs.map((message, index) => (
                  <div
                    key={index}
                    className={`flex items-center ${
                      message.sentByMe ? "justify-end" : "justify-start"
                    }`}
                  >
                    {!message.sentByMe && (
                      <img
                        src={message.userProfilePic ?? dpIcon}
                        alt="User DP"
                        className="w-8 h-8 rounded-full mr-2"
                      />
                    )}
                    <div
                      className={`p-2 flex items-end gap-3 rounded-xl shadow-md bg-lightWhiteBg text-lightBlackText min-w-0 ${
                        message.sentByMe ? "max-w-[70%]" : "max-w-sm"
                      }`}
                    >
                      <div className="flex flex-col w-full min-w-0">
                        <p className="break-words overflow-wrap-anywhere">
                          {message.message}
                        </p>
                        <p className="text-xs text-right text-lightBlackText mt-1 whitespace-nowrap">
                          {moment(message.createdAt).format("hh:mm A")}
                        </p>
                      </div>
                    </div>
                    {message.sentByMe && (
                      <img
                        src={userProfilePic ?? undefined}
                        alt="User DP"
                        className="w-8 h-8 rounded-full ml-2"
                      />
                    )}
                  </div>
                ))}
              </Fragment>
            ))}
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default React.memo(AmbassadorChatWindow);
